import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import currencyUtils, { formatAmount } from '../utils/currencyUtils';
import { validateClientId as validateClientIdUtil } from '../utils/validationClient';
import { useTracking } from '../hooks/useTracking.js';
import moment from 'moment';
import 'moment/locale/be';
import 'moment/locale/ca';
import 'moment/locale/cs';
import 'moment/locale/de-at';
import 'moment/locale/de-ch';
import 'moment/locale/en-au';
import 'moment/locale/en-ca';
import 'moment/locale/es';
import 'moment/locale/eu';
import 'moment/locale/fr-ca';
import 'moment/locale/fr-ch';
import 'moment/locale/fr';
import 'moment/locale/hu';
import 'moment/locale/it-ch';
import 'moment/locale/ko';
import 'moment/locale/nl-be';
import 'moment/locale/nl';
import 'moment/locale/pt';
import 'moment/locale/ru';
import 'moment/locale/sk';
import 'moment/locale/sl';
export const GlobalContext = createContext();

export const useGlobalContext = () => {
  return useContext(GlobalContext);
};

export const GlobalProvider = ({ children }) => {
  const { t } = useTranslation();

  const [store, setStore] = useState(() => {
    const savedStore = sessionStorage.getItem('store');
    if (savedStore) {
      const parsedStore = JSON.parse(savedStore);
      // Si c'est une enseigne pro, clean le professionalUser partiel du cache 
      if (parsedStore.isProSignboard && parsedStore.professionalUser && !parsedStore.professionalUser.firstname) {
        delete parsedStore.professionalUser;
        sessionStorage.setItem('store', JSON.stringify(parsedStore));
      }
      return parsedStore;
    }
    return {};
  });
  const [professionalUser, setProfessionalUser] = useState(null);
  const [customParams, setCustomParams] = useState(() => {
    const savedParams = sessionStorage.getItem('customParams');
    return savedParams ? JSON.parse(savedParams) : {};
  });

  const [loading, setLoading] = useState(!sessionStorage.getItem('store'));
  const [error, setError] = useState(null);
  const [selectedLang, setSelectedLang] = useState(() =>
    sessionStorage.getItem('lang') || 'en');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [currencyPosition, setCurrencyPosition] = useState('11');
  const [currencyData, setCurrencyData] = useState({});
  const [languages, setLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [defaultLanguage, setDefaultLanguage] = useState(null);
  const [user, setUser] = useState({ clientId: null });
  const [applyDiscount, setApplyDiscount] = useState(false); 

  const LOCALE_MAPPING = {
    'be': 'be',           
    'ca': 'ca',           
    'cs': 'cs',           
    'de_AT': 'de-at',    
    'de_CH': 'de-ch',     
    'en_AU': 'en-au',     
    'en_CA': 'en-ca',    
    'en_US': 'en',        
    'en': 'en',          
    'es': 'es',          
    'eu': 'eu',           
    'fr_CA': 'fr-ca',     
    'fr_CH': 'fr-ch',     
    'fr': 'fr',          
    'hu': 'hu',           
    'it_CH': 'it-ch',     
    'ko': 'ko',          
    'nl_BE': 'nl-be',     
    'nl': 'nl',           
    'pt': 'pt',           
    'ru': 'ru',           
    'sk': 'sk',           
    'sl': 'sl'            
  };
  const [selectedStore, setSelectedStore] = useState(() => {
    return sessionStorage.getItem('selectedStore') || '';
  });

  const [cookieAccepted, setCookieAccepted] = useState(() => {
    const saved = localStorage.getItem('cookieAccepted');
    return saved === 'true';
  });
  const { prepareTracking, addTracking, trackingAdded } = useTracking(store, cookieAccepted);
  const [step, setStep] = useState(() => {
    const saved = sessionStorage.getItem('step');
    return saved ? Number(saved) : 1;
  });


  const initializeCurrencyData = useCallback(async () => {
    try {
      const data = await currencyUtils.loadCurrencyData();
      setCurrencyData(data);
      return data;
    } catch (error) {
      console.error('Error loading currency data:', error);
      return {};
    }
  }, []);


  const validateClientId = (value) => {
    return validateClientIdUtil(value, store.clientIdType, store.isClientIdOptional);
  };

  const checkProfessionalDiscount = useCallback(async (proIdentification) => {
    if (!store.isProSignboard || !proIdentification) {
      return null;
    }

    try {
      const graphics = process.env.REACT_APP_GRAPHICS_API_URL;
      const apiKey = process.env.REACT_APP_API_KEY;
      const storeType = store.digital ? 'digital' : 'physical';
      
      const apiUrl = `${graphics}/api/signboardpro/discount?store=${storeType}&signboardId=${store.id}&proIdentification=${encodeURIComponent(proIdentification)}&api_key=${apiKey}`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`Erreur API : ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status === 'ok' && data.result && data.result.discount !== undefined) {
        return data.result.discount;
      }

      return null;
    } catch (error) {
      console.error('Erreur lors de la vérification de la remise:', error);
      return null;
    }
  }, [store.isProSignboard, store.digital, store.id]);

  useEffect(() => {
    if (store.discount?.eligibility === 2 && user?.clientId) {
      const isValid = validateClientId(user.clientId);
      setApplyDiscount(isValid);
    } else if (store.discount?.eligibility === 0) {
      setApplyDiscount(true);
    }
  }, [store.discount, user?.clientId, validateClientId]);

  function getBaseUrl() {
    const fullUrl = window.location.href;
    const url = new URL(fullUrl);
    return url.origin;
  }
  useEffect(() => {
    const stepMap = { 1: 'home', 2: 'cart' };
    if (stepMap[step]) {
      console.log('Auto-tracking:', step, '→', stepMap[step]);
      prepareTracking(stepMap[step]);
    }
  }, [step, prepareTracking]);
  const fetchStoreData = useMemo(() => async () => {
    try {
      const baseURL = process.env.REACT_APP_ENV == "localhost" ? process.env.REACT_APP_BASE_URL : getBaseUrl();
      const graphics = process.env.REACT_APP_GRAPHICS_API_URL;
      const apiKey=process.env.REACT_APP_API_KEY;
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      const apiUrl = `${graphics}/api/signboard/graphics?url=${baseURL}&mobile=${isMobile ? '1' : '0'}&api_key=${apiKey}`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`Erreur API : ${response.statusText}`);
      }

      const data = await response.json();
      if (data.status === 'ko' || data.isAvailable === false) {
        const storeData = {
          ...data.result,
          exception: data.errors,
          isAvailable: false
        };
        setStore(storeData);
        throw new Error(data.errors[0]?.message || 'Une erreur est survenue');
      }

      let storeData = {
        ...data.result,
        allowAmex: data.allowAmex,
        panier: data.panier,
        b2b2c: data.b2b2c,
        isAvailable: data.isAvailable,
        errors: [],
        cssUrls: [data.result.css, data.result.user_css].filter(Boolean)
      };
      if (storeData.b2b2c) {
        
        const urlParams = new URLSearchParams(window.location.search);
        const encodedData = urlParams.get('d');
        
        if (encodedData && encodedData !== 'null') {
          try {
            const decodedData = JSON.parse(atob(encodedData));
            
            const {
              lastname, firstname, email, client, discount, language, callback, key, limit_card, limit_amount
            } = decodedData;

            if (lastname && firstname && email && client && discount && language && callback && key) {
              const professionalUserData = {
                lastname,
                firstname,
                email,
                confEmail: email,
                client,
                discount,
                limit_card,
                limit_amount,
                callback,
                key,
                language
              };

              storeData = {
                ...storeData,
                professionalUser: professionalUserData
              };

              setUser(professionalUserData);

              sessionStorage.setItem('professionalUser', JSON.stringify(professionalUserData));
              sessionStorage.setItem('d', encodedData);
            } 
          } catch (error) {
            console.error('décodage professionalUser:', error);
          }
        } 
      } 

      initializeLanguage(storeData);
      setGlobalData(data.result);
      setCustomParams(data.result);
      sessionStorage.setItem('store', JSON.stringify(storeData));
      sessionStorage.setItem('customParams', JSON.stringify(data.result));
      setStore(storeData)
      setTimeout(() => {
        setLoading(false);
      }, 500);
    } catch (err) {
      setStore(prevStore => ({
        ...prevStore,
        errors: [{ message: t('Le service est en maintenance et momentanément interrompu. Veuillez nous excuser pour le désagrément occasionné, nous travaillons à sa remise en ligne dans les plus brefs délais. Nous vous invitons à réessayer ultérieurement.') }],
        isAvailable: false
      }));
      setError(err.message || 'Une erreur est survenue lors du chargement des données.');
    }
  }, []);
  const setMoment = (lang) => {
    try {
      const momentLocale = LOCALE_MAPPING[lang];
      if (momentLocale) {
        moment.locale(momentLocale);
      } else {
        moment.locale('en');
      }

    } catch (error) {
      moment.locale('en');
    }
  }
  const initializeLanguage = (storeData) => {
    if (storeData.languages && storeData.languages.length > 0) {
      setLanguages(storeData.languages);
      const savedLang = sessionStorage.getItem('lang');
      const defaultLang = storeData.default_language;

      let langToUse;

      if (savedLang) {
        langToUse = storeData.languages.find(l => l.code === savedLang);
      }

      if (!langToUse) {
        langToUse = storeData.languages.find(l => l.code === defaultLang) || storeData.languages[0];
      }
      i18n.changeLanguage(langToUse.code);
      setDefaultLanguage(defaultLang);
      setCurrentLanguage(langToUse);
      setCurrencyPosition(langToUse.currencyPosition || '11');
      setMoment(langToUse.code)
      sessionStorage.setItem('lang', langToUse.code);
    }
  };

  const updateProfessionalDiscount = useCallback((newDiscount) => {
    setStore(prevStore => {
      if (prevStore.professionalUser?.firstname) {
        // B2B2C: update existing professionalUser's discount
        return {
          ...prevStore,
          professionalUser: {
            ...prevStore.professionalUser,
            discount: newDiscount
          }
        };
      }
      // Pro signboard: store discount separately, don't create fake professionalUser
      return {
        ...prevStore,
        proSignboardDiscount: newDiscount
      };
    });
  }, []);

  useEffect(() => {
    fetchStoreData();
  }, [fetchStoreData]);
  useEffect(() => {
    initializeCurrencyData();
  }, [initializeCurrencyData]);


  const contextValue = useMemo(() => ({
    store,
    customParams,
    loading,
    error,
    step,
    selectedLang,
    modalVisible,
    modalContent,
    globalData,
    cookieAccepted,
    currentLanguage,
    languages,
    defaultLanguage,
    prepareTracking,
    addTracking,
    trackingAdded,
    cookieAccepted,
    setCookieAccepted,
    validateClientId,
    checkProfessionalDiscount,
    updateProfessionalDiscount,
    setUser,
    currencyData,
    user,
    applyDiscount,
    selectedStore,
    professionalUser,

    changeLanguage: (langCode) => {
      const newLang = languages.find(l => l.code === langCode);
      if (newLang) {
        setCurrentLanguage(newLang);
        setCurrencyPosition(newLang.currencyPosition || '01');
        sessionStorage.setItem('lang', langCode);
        setMoment(langCode)
      }
      setSelectedLang(langCode);
    },
    goToStep: (targetStep) => {
      setStep(targetStep);
      sessionStorage.setItem('step', targetStep);
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant"
      });
    },
    setCurrentStore: (s) => {
      setSelectedStore(s);
      sessionStorage.setItem('selectedStore', s);
    },
    showModal: (content) => {
      setModalContent(content);
      setModalVisible(true);
    },
    hideModal: () => {
      setModalVisible(false);
      setModalContent(null);
    },

    formatAmounts: (amount, decimals = 0) => {
      const storeWithCurrency = {
        ...store,
        currencyData,
        devise: store.devise || 'EUR'
      };
      return formatAmount(amount, decimals, storeWithCurrency, currentLanguage);
    },
    acceptCookie: () => {
      setCookieAccepted(true);
      localStorage.setItem('cookieAccepted', 'true');

      const event = new Event('cookieAccepted');
      window.dispatchEvent(event);
    },

    refuseCookie: () => {
      setCookieAccepted(false);
      localStorage.setItem('cookieAccepted', 'false');
    },

    updateProfessionalUser: (updates) => {
      if (professionalUser) {
        const updatedUser = { ...professionalUser, ...updates };
        setProfessionalUser(updatedUser);
        sessionStorage.setItem('professionalUser', JSON.stringify(updatedUser));
      }
    },
    clearProfessionalUser: () => {
      setProfessionalUser(null);
      sessionStorage.removeItem('professionalUser');
      sessionStorage.removeItem('d');
    }
  }), [store, customParams, loading, error, step, selectedLang, modalVisible, user,
    modalContent, globalData, cookieAccepted, prepareTracking, addTracking, trackingAdded, currencyPosition, professionalUser, currencyData, checkProfessionalDiscount, updateProfessionalDiscount]);

  return (
    <GlobalContext.Provider value={contextValue}>
      {loading && !sessionStorage.getItem('store') ? (
        <div id="preloader">
          <div id="loader"></div>
          {error && <div className="loading-error">Error: {error}</div>}
        </div>
      ) : (
          children
        )}
    </GlobalContext.Provider>
  );
};